import { ReactElement, ReactNode } from 'react'
import produce from 'immer'
import create from 'zustand'
import createContext from 'zustand/context'
import { combine } from 'zustand/middleware'
import type { PlanViewPlanChoice } from '@editor/pages/PlanView/constants'
import { validate } from '../middleware'

export type ResultsViewMode = 'comments' | 'tensile-connectors-table' | 'blockpad'
export type ResultsViewTab =
  | 'walls'
  | 'slab-beams'
  | 'slabs'
  | 'roof-slab-beams'
  | 'roof-slabs'
  | 'beams'
  | 'purlins'
  | 'columns'
  | 'foundation'
  | 'comments'
export type ResultsViewWallSubTab =
  | 'wall-assemblies'
  | 'wall-rips'
  | 'wall-lintels'
  | 'stiffening'
  | 'anchors'
  | 'shear-anchors'
  | 'standard-rips'
/**
 * Active tab inside the Results2 detail pane. `'surface'` is the merged Dach/Decke tab on the
 * slab-beam pane that shows the associated vertical (roof) slab. Lifted into the store so the
 * 3D scene can render the matching loads and so selecting a slab can open it directly.
 */
export type ResultsDetailTab = 'design' | 'vertical-connections' | 'documents' | 'surface'

/** Sidebar tab of the "Planansicht" page. */
export type PlanViewTab = 'preview' | 'plans' | 'tables' | 'comments' | 'image-cards'

/**
 * Whether the plan view's scene is covered: by the "Pläne" tab's PDF preview, or by the notice
 * for "Lastplan", which has no preview. Only meaningful while the plan view page is open.
 */
export const selectIsPlanViewSceneCovered = (state: {
  planView: { tab: PlanViewTab; planChoice: PlanViewPlanChoice }
}): boolean => state.planView.tab === 'plans' || state.planView.planChoice === 'loadPlan'

interface Props {
  children: ReactNode
}

/**
 * View (tab/pane) state of the Results pages. The selected elements themselves live in the global
 * selection store — use `useResultsSelection` (`@editorHooks`) for combined selection+tab updates;
 * this store only keeps which tabs/panes are open around that selection.
 */
interface EditorViewInitialState {
  results: {
    tab: ResultsViewTab
    wallSubTab: ResultsViewWallSubTab
    detailTab: ResultsDetailTab
    /**
     * Raw load guids of the load row whose origin is being traced in the scene (for summarized
     * rows all original load guids). null = trace mode off.
     */
    tracedLoads: string[] | null
    loadTab: LoadType | null
    localTab: string | null
    visibleLoadTypes: Record<LoadType, boolean>
    isMultiEditMode: boolean
    /** "Relevante Schubanker" panel in the Schubanker tab's detail area. */
    shearAnchorSelectionOpen: boolean
  }
  /** "Planansicht" sidebar; kept for the session so leaving and re-entering the page restores it. */
  planView: {
    tab: PlanViewTab
    /** Plan highlighted in the "Pläne" tab (e.g. the one just generated). */
    selectedPlanGuid: string | null
    /**
     * Plantyp chosen in the sidebar. The previewable types drive the scene (via the plan preset);
     * "loadPlan" can only be generated and covers the scene with a notice instead.
     */
    planChoice: PlanViewPlanChoice
    /** "Plan nach Erzeugung herunterladen": download the PDF once "Plan generieren" is done. */
    downloadAfterGenerate: boolean
  }
}

interface EditorViewStoreType extends EditorViewInitialState {
  setResultsTab: (tab: ResultsViewTab, wallSubTab?: ResultsViewWallSubTab) => void
  setResultsWallSubTab: (wallSubTab: ResultsViewWallSubTab) => void
  setResultsDetailTab: (detailTab: ResultsDetailTab) => void
  setResultsTracedLoads: (loadGuids: string[] | null) => void
  setResultsLoadTab: (loadTab: LoadType | null) => void
  setResultsLocalTab: (localTab: string | null) => void
  setResultsVisibleLoadType: (loadType: LoadType, visible: boolean) => void
  setResultsIsMultiEditMode: (isMultiEditMode: boolean) => void
  setResultsShearAnchorSelectionOpen: (open: boolean) => void
  setPlanViewTab: (tab: PlanViewTab) => void
  setPlanViewSelectedPlanGuid: (guid: string | null) => void
  setPlanViewPlanChoice: (planChoice: PlanViewPlanChoice) => void
  setPlanViewDownloadAfterGenerate: (downloadAfterGenerate: boolean) => void
}

const initialState: EditorViewInitialState = {
  results: {
    tab: 'walls',
    wallSubTab: 'wall-rips',
    detailTab: 'design',
    tracedLoads: null,
    loadTab: null,
    localTab: null,
    visibleLoadTypes: {
      'point-load': true,
      'line-load': true,
      'area-load': true,
    },
    isMultiEditMode: false,
    shearAnchorSelectionOpen: false,
  },
  planView: {
    tab: 'preview',
    selectedPlanGuid: null,
    planChoice: 'positionPlan',
    downloadAfterGenerate: true,
  },
}

const createStore = () =>
  create(
    validate(
      combine(initialState, set => ({
        setResultsTab: (tab: ResultsViewTab, wallSubTab?: ResultsViewWallSubTab) =>
          set(
            produce(state => {
              state.results.tab = tab
              if (wallSubTab) state.results.wallSubTab = wallSubTab
              state.results.detailTab = 'design'
              state.results.tracedLoads = null
              state.results.isMultiEditMode = false
              state.results.shearAnchorSelectionOpen = false
            }),
          ),

        setResultsWallSubTab: (wallSubTab: ResultsViewWallSubTab) =>
          set(
            produce(state => {
              state.results.tab = 'walls'
              state.results.wallSubTab = wallSubTab
              state.results.detailTab = 'design'
              state.results.tracedLoads = null
              state.results.isMultiEditMode = false
              state.results.shearAnchorSelectionOpen = false
            }),
          ),

        setResultsDetailTab: (detailTab: ResultsDetailTab) =>
          set(
            produce(state => {
              state.results.detailTab = detailTab
            }),
          ),

        setResultsTracedLoads: (tracedLoads: string[] | null) =>
          set(
            produce(state => {
              state.results.tracedLoads = tracedLoads
            }),
          ),

        setResultsLoadTab: (loadTab: LoadType | null) =>
          set(
            produce(state => {
              state.results.loadTab = loadTab
            }),
          ),

        setResultsLocalTab: (localTab: string | null) =>
          set(
            produce(state => {
              state.results.localTab = localTab
            }),
          ),

        setResultsVisibleLoadType: (loadType: LoadType, visible: boolean) =>
          set(
            produce(state => {
              state.results.visibleLoadTypes[loadType] = visible
            }),
          ),

        setResultsIsMultiEditMode: (isMultiEditMode: boolean) =>
          set(
            produce(state => {
              state.results.isMultiEditMode = isMultiEditMode
            }),
          ),

        setResultsShearAnchorSelectionOpen: (open: boolean) =>
          set(
            produce(state => {
              state.results.shearAnchorSelectionOpen = open
            }),
          ),

        setPlanViewTab: (tab: PlanViewTab) =>
          set(
            produce(state => {
              state.planView.tab = tab
            }),
          ),

        setPlanViewSelectedPlanGuid: (guid: string | null) =>
          set(
            produce(state => {
              state.planView.selectedPlanGuid = guid
            }),
          ),

        setPlanViewPlanChoice: (planChoice: PlanViewPlanChoice) =>
          set(
            produce(state => {
              state.planView.planChoice = planChoice
            }),
          ),

        setPlanViewDownloadAfterGenerate: (downloadAfterGenerate: boolean) =>
          set(
            produce(state => {
              state.planView.downloadAfterGenerate = downloadAfterGenerate
            }),
          ),
      })),
    ),
  )

const { Provider, useStore: useEditorViewStore } = createContext<EditorViewStoreType>()

const EditorViewStoreProvider = ({ children }: Props): ReactElement => (
  <Provider createStore={createStore}>{children}</Provider>
)

export { EditorViewStoreProvider, useEditorViewStore }
